import { useAuth } from '@/auth/AuthContext';
import { RootState } from '@/typings';
import { ElButton, ElForm, ElInput, ElLoading, ElMessage, ElProgress, FormRules, useClassNames, useForm } from '@qsxy/element-plus-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updatePwd, valiOldPwd } from './api';
import { WeakPassword } from './passwordUtil';
import usePasswordForce from './usePasswordForce';

enum ForceMap {
	high = 100,
	middle = 65,
	low = 30,
}
enum ForceColorMap {
	high = 'var(--el-color-success)',
	middle = 'var(--el-color-warning)',
	low = 'var(--el-color-error)',
}

const SafeSetting = () => {
	const loginUser = useSelector<RootState, RootState['user']>(state => state.user);
	const [formInstance] = useForm<{ password: string; username: string }>();

	const { b } = useClassNames('setting', 'ttn');
	const PasswordForce = usePasswordForce();
	const { autoSignOut } = useAuth();
	const navigate = useNavigate();

	const rules = useMemo<FormRules>(() => {
		return {
			oldPassword: [
				{ required: true, message: '请填写当前账号密码' },
				{
					validator(_, value) {
						if (!value) {
							return Promise.reject(new Error('请填写当前账号密码'));
						}
						return valiOldPwd({ username: loginUser?.username, password: value });
					},
					message: '您输入的旧密码不正确！',
				},
			],
			password: [
				{ required: true, message: '请填写新密码' },
				{
					validator(_, value) {
						if (value === formInstance.getFieldValue('oldPassword')) {
							return Promise.reject(new Error('不能使用旧密码！'));
						}

						PasswordForce.validate(value);
						const checkResult = WeakPassword(loginUser.username, value);
						if (checkResult) {
							return Promise.reject(new Error(checkResult));
						}

						return Promise.resolve();
					},
				},
			],
			passwordConfirm: [
				{ required: true, message: '请重复输入新密码' },
				{
					validator: (_, value) => {
						if (value !== formInstance.getFieldValue('password')) {
							return Promise.reject(new Error('您两次输入的密码不一致！'));
						}
						return Promise.resolve();
					},
				},
			],
		};
	}, [PasswordForce, formInstance, loginUser?.username]);

	const customColorMethod = useCallback(() => {
		if (PasswordForce.force) {
			return ForceColorMap[PasswordForce.force];
		}
		return 'var(--el-color-info)';
	}, [PasswordForce.force]);

	return (
		<>
			<h3 className="title">安全设置</h3>
			<section className={b()}>
				<main className={b`main`}>
					<ElForm form={formInstance} colon labelWidth={140} style={{ marginTop: 20, maxWidth: 500 }} rules={rules}>
						<ElForm.Item label="登录密码" name="oldPassword" validateTrigger="onBlur">
							<ElInput placeholder="请填写当前账号密码" type="password" showPassword />
						</ElForm.Item>

						<ElForm.Item label="设置新密码" name="password">
							<ElInput placeholder="请设置新密码" type="password" showPassword />
						</ElForm.Item>
						<ElForm.Item>
							<ElProgress percentage={PasswordForce.force ? ForceMap[PasswordForce.force] : 0} color={customColorMethod} style={{ width: '100%' }}>
								{PasswordForce.force ? <PasswordForce.View color={ForceColorMap[PasswordForce.force]} /> : <span>密码强度</span>}
							</ElProgress>
						</ElForm.Item>

						<ElForm.Item label="确认新密码" name="passwordConfirm">
							<ElInput placeholder="请重复输入新密码" type="password" showPassword />
						</ElForm.Item>

						<ElForm.Item center labelWidth={0}>
							<ElButton
								type="primary"
								onClick={() => {
									formInstance.validateFields().then(user => {
										updatePwd({ username: loginUser.username, ...user })
											.then(() => {
												const load = ElLoading.service({
													text: '密码修改成功，即将自动跳转到登录页面',
												});
												setTimeout(() => {
													load.close();
													autoSignOut(navigate);
												}, 1000);
											})
											.catch(() => ElMessage.error('保存失败！'));
									});
								}}
							>
								立即修改
							</ElButton>
						</ElForm.Item>
					</ElForm>
				</main>
			</section>
		</>
	);
};

const LoadingText = () => {
	const [timeOut, setTimeOut] = useState(3);
	// eslint-disable-next-line no-undef
	const timeOutRef = useRef<NodeJS.Timer>(null);
	const timeRef = useRef<number>(3);

	useEffect(() => {
		timeOutRef.current = setInterval(() => {
			timeRef.current = timeRef.current - 1;
			timeRef.current = timeRef.current <= 0 ? 1 : timeRef.current;
			setTimeOut(timeRef.current);
		}, 1000);

		return () => {
			clearInterval(timeOutRef.current);
		};
	}, [timeOut]);

	return <>密码修改成功，{timeOut}秒后自动跳转到登录页面</>;
};

export default SafeSetting;
